/**
 * PersonaCard — the shareable "I AM A …" archetype card that closes the
 * reveal. Everything derives deterministically from the scan data: four
 * trait sliders (budget↔luxury, homebody↔nomad, new spots↔the usuals,
 * lone wolf↔connector), an archetype named from the dominant traits, a
 * stat strip, and obsession chips from the sent-mail topics.
 */
import type { TripRow } from './TravelReport';
import type { ReservationRow } from './ReservationsReport';

export interface PersonaInputs {
  trips: TripRow[];
  reservations: ReservationRow[];
  /** Hotel/lodging names surfaced by the trips. */
  lodgings: string[];
  sentTopics: Array<{ topic: string; count: number }>;
  innerCircleSize?: number;
  contactCount?: number;
  /** "Bitcoin author", "240K on X" style public-presence fragments. */
  presence?: string[];
}

const LUXURY_RE = /\b(w\b|palazzo|venetian|four seasons|ritz|peninsula|aman|rosewood|regina|grand h[oô]tel|pavillon|spa|adelphi|newbury|dominick|st\.? regis|edition|soho house)\b/i;
const BUDGET_RE = /\b(hampton|holiday inn|motel|best western|comfort inn|travelodge|super 8|days inn)\b/i;

const TOPIC_CHIP: Record<string, string> = {
  'bitcoin/crypto': 'BITCOIN',
  'book/publishing': 'WRITING',
  'fundraising/VC': 'INVESTING',
  'legal/tax': 'LEGAL',
  'sales/deals': 'DEALS',
  'hiring': 'HIRING',
  'travel': 'TRAVEL',
  'product/eng': 'BUILDING',
  'events/orders': 'EVENTS',
};

export function derivePersona(d: PersonaInputs) {
  const past = d.trips.filter((t) => !t.planned);
  const nights = past.reduce((n, t) => {
    if (!t.startISO) return n;
    const e = new Date(t.endISO ?? t.startISO).getTime();
    const s = new Date(t.startISO).getTime();
    return n + Math.max(1, Math.round((e - s) / 86_400_000));
  }, 0);

  // Sliders, 0..1 (right pole = the labeled extreme).
  const lux = d.lodgings.filter((l) => LUXURY_RE.test(l)).length;
  const bud = d.lodgings.filter((l) => BUDGET_RE.test(l)).length;
  const luxury = d.lodgings.length === 0 ? 0.5 : Math.min(1, 0.25 + (lux - bud * 0.5) / Math.max(d.lodgings.length, 1) * 0.9);
  const nomad = Math.min(1, nights / 55);
  const venueCounts = new Map<string, number>();
  for (const r of d.reservations) {
    const k = r.name.toLowerCase();
    venueCounts.set(k, (venueCounts.get(k) ?? 0) + 1);
  }
  const repeatBookings = [...venueCounts.values()].filter((n) => n > 1).reduce((a, b) => a + b, 0);
  const usuals = d.reservations.length === 0 ? 0.5 : Math.min(1, 0.3 + (repeatBookings / d.reservations.length) * 1.2);
  const circle = d.innerCircleSize ?? 0;
  const connector = Math.min(1, 0.25 + circle / 12);

  // Archetype from the dominant pair.
  let name = 'Steady Operator';
  let blurb = 'You keep a tight orbit — the work, the places, and the people are deliberate.';
  if (nomad >= 0.5 && usuals >= 0.55) {
    name = 'Jet-Set Regular';
    blurb = "Always moving, but you've got your places — your kind of hotels, the same handful of restaurants, and a tight crew you actually keep up with.";
  } else if (nomad >= 0.5 && usuals < 0.55) {
    name = 'Explorer';
    blurb = "Always moving, always somewhere new — new cities, new tables, and a network that spans them.";
  } else if (nomad < 0.5 && usuals >= 0.55) {
    name = 'Creature of Habit';
    blurb = 'Home base, favorite tables, people you actually see — you run deep, not wide.';
  }

  const countries = new Set(allCountryOf(d.trips)).size;
  const tables = venueCounts.size;
  const regulars = [...venueCounts.values()].filter((n) => n >= 2).length;
  const obsessions = d.sentTopics
    .slice(0, 3)
    .map((t) => TOPIC_CHIP[t.topic] ?? t.topic.split('/')[0].toUpperCase())
    .filter(Boolean);

  return {
    name,
    blurb,
    sliders: [
      { left: 'Budget', right: 'Luxury', v: luxury },
      { left: 'Homebody', right: 'Nomad', v: nomad },
      { left: 'New spots', right: 'The usuals', v: usuals },
      { left: 'Lone wolf', right: 'Connector', v: connector },
    ],
    stats: [
      [countries, countries === 1 ? 'country' : 'countries'],
      [tables, 'tables'],
      [regulars, 'regulars'],
      [d.contactCount ?? circle, 'in circle'],
    ] as Array<[number, string]>,
    obsessions,
  };
}

const FRENCH = new Set(['PARIS', 'CHAMONIX', 'CHATEAU', 'FRANCE', 'BIARRITZ', 'NICE', 'CDG']);
function allCountryOf(trips: TripRow[]): string[] {
  return trips.map((t) => {
    const words = (t.city ?? t.destination).toUpperCase().match(/[A-ZÀ-Ö]{3,}/g) ?? [];
    return words.some((w) => FRENCH.has(w)) ? 'France' : 'United States';
  });
}

export function PersonaCard({ inputs }: { inputs: PersonaInputs }) {
  const p = derivePersona(inputs);
  return (
    <div className="opc2">
      <div className="opc2-inner">
        <div className="otr-label">I am a</div>
        <div className="opc2-name">{p.name}</div>
        <p className="opc2-blurb">{p.blurb}</p>
        {inputs.presence && inputs.presence.length > 0 && (
          <div className="opc2-presence">
            <span className="opc2-presence-dot" />
            {['Public presence', ...inputs.presence].join(' · ')}
          </div>
        )}
        <div className="opc2-sliders">
          {p.sliders.map((s) => (
            <div className="opc2-slider" key={s.right}>
              <div className="opc2-slider-labels">
                <span className="opc2-pole">{s.left}</span>
                <span className="opc2-pole opc2-pole--active">{s.right}</span>
              </div>
              <div className="opc2-track">
                <span className="opc2-thumb" style={{ left: `${Math.round(s.v * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="opc2-stats">
        {p.stats.map(([n, label]) => (
          <div className="opc2-stat" key={label}>
            <div className="otr-stat-n">{n}</div>
            <div className="otr-stat-label">{label}</div>
          </div>
        ))}
      </div>
      {p.obsessions.length > 0 && (
        <div className="opc2-obsessions">
          <div className="otr-label">Obsessions</div>
          <div className="opc2-chips">
            {p.obsessions.map((o) => <span className="opc2-chip" key={o}>{o}</span>)}
          </div>
        </div>
      )}
      <div className="opc2-footer">
        <span className="opc2-brand">XAVIOR</span>
        <span className="opc2-tag">what's your card? · xavior.ai</span>
      </div>
    </div>
  );
}
